import { memo, Ref, useCallback, useImperativeHandle, useLayoutEffect, useState } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Input, setInputAutoWidth, Stack, Typography, TypographyType } from '@lyearn/ui';
import getFileNameAndExtension from '@/modules/classrooms/modules/files/utils/getFileNameAndExtension';

import { useMediaContext } from '../context';

export type MediaNameComponentRef = {
  startRename: () => void;
};

interface NameProps {
  showFileExtension: boolean;
  className?: string;
  classes?: {
    name?: string;
    extension?: string;
  };
  componentProps?: {
    name?: TypographyType;
    extension?: TypographyType;
  };
  onRename?: (newName: string) => void;
  componentRef?: Ref<MediaNameComponentRef | undefined>;
}

function MediaName(props: NameProps) {
  const { showFileExtension, className, classes, componentProps, onRename, componentRef } = props;
  const { media } = useMediaContext();
  const { name, extension } = getFileNameAndExtension(media.name);

  const [newName, setNewName] = useState(name);
  const [isRenaming, { on: startRename, off: endRename }] = useToggle(false);
  const isOptimisticName = !isRenaming && name !== newName.trim();

  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  useLayoutEffect(() => {
    if (inputRef) {
      setInputAutoWidth(inputRef);
    }
    // newName is required dependency to ensure the input width updates when the name changes
  }, [inputRef, newName]);

  useImperativeHandle(componentRef, () => ({
    startRename,
  }));

  const cancelRename = useCallback(() => {
    setNewName(name);
    endRename();
  }, [endRename, name]);

  const renameMediaItem = useCallback(() => {
    if (!inputRef) {
      return;
    }
    const trimmedName = newName.trim();
    if (trimmedName.length && trimmedName !== name) {
      // rename if new name exists and is different from the current name
      onRename?.(trimmedName + '.' + extension);
      endRename();
    } else {
      cancelRename();
    }
  }, [cancelRename, endRename, extension, inputRef, name, newName, onRename]);

  const onNameChange = useEvent<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    setNewName(e.target.value);
  });

  const onClick = useEvent<React.MouseEventHandler<HTMLInputElement>>(async (e) => {
    e.stopPropagation();
  });

  const onBlur = useEvent(async () => {
    renameMediaItem();
  });

  const onKeyDown = useEvent<React.KeyboardEventHandler<HTMLInputElement>>((e) => {
    // Stop containing modal from closing.
    e.stopPropagation();
  });

  const onKeyUp = useEvent<React.KeyboardEventHandler<HTMLInputElement>>((e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      renameMediaItem();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      cancelRename();
    }
  });

  return (
    <Stack className={className}>
      {isRenaming ? (
        <Input
          autoFocus
          characterLimit={70}
          className={'border-[0px] bg-transparent rounded-none'}
          componentsProps={{
            input: {
              className: cn('!p-0', classes?.name),
              style: {
                minWidth: '3ch',
              },
            },
            root: {
              className: '!min-h-0 text-text-primary',
            },
          }}
          defaultValue={name}
          inputRef={setInputRef}
          onBlur={onBlur}
          onChange={onNameChange}
          onClick={onClick}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
        />
      ) : (
        <TypographyWithTooltip
          className={classes?.name}
          color={isOptimisticName ? 'text-text-secondary' : 'text-text-primary'}
          numberOfLines={1}
          variant="body-short-02"
          {...componentProps?.name}>
          {isOptimisticName ? newName : name}
        </TypographyWithTooltip>
      )}
      {showFileExtension && extension ? (
        <Typography
          className={classes?.extension}
          color={isOptimisticName ? 'text-text-secondary' : 'text-text-primary'}
          variant="body-short-02"
          {...componentProps?.extension}>
          {`.${extension}`}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default memo(MediaName);
